/* ==========================================================================
   Zayma — Floating product video (minimal circular reel preview + modal)

   Loaded ONLY when the current product has a video, because the markup that
   this script looks for is only rendered in that case. It adds no polling,
   no MutationObserver and no external library, and it never touches the
   product form, variant picker, cart or checkout.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT_ID = 'ZaymaFloatingVideo';
  var HINT_MS = 6000;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function idle(fn, timeout) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: timeout || 2000 });
    } else {
      setTimeout(fn, 200);
    }
  }

  function prefersReducedMotion() {
    return (
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /* Never stream the preview on a metered or very slow connection — the
     poster frame plus play icon is shown instead, and the full video is
     still one tap away. */
  function constrainedConnection() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    var t = c.effectiveType || '';
    return t === 'slow-2g' || t === '2g';
  }

  function ZaymaFloatingVideo(root) {
    this.root = root;
    this.bubble = root.querySelector('[data-zfv-bubble]');
    this.preview = root.querySelector('[data-zfv-preview]');
    this.modal = root.querySelector('[data-zfv-modal]');
    this.dialog = root.querySelector('[data-zfv-dialog]');
    this.video = root.querySelector('[data-zfv-video]');
    this.playPauseBtn = root.querySelector('[data-zfv-playpause]');
    this.muteBtn = root.querySelector('[data-zfv-mute]');
    this.hint = root.querySelector('[data-zfv-hint]');
    this.closeBtns = root.querySelectorAll('[data-zfv-close]');

    this.previewSrc = root.getAttribute('data-preview-src') || '';
    this.fullSrc = root.getAttribute('data-full-src') || '';
    this.autoplay = root.getAttribute('data-autoplay') === 'true';
    this.loop = root.getAttribute('data-loop') === 'true';
    this.delay = parseFloat(root.getAttribute('data-delay') || '0') * 1000;
    this.startMuted = root.getAttribute('data-sound') !== 'on';

    this.labels = {
      play: root.getAttribute('data-label-play') || 'Play video',
      pause: root.getAttribute('data-label-pause') || 'Pause video',
      mute: root.getAttribute('data-label-mute') || 'Mute video',
      unmute: root.getAttribute('data-label-unmute') || 'Unmute video'
    };

    this.isOpen = false;
    this.lastFocus = null;
    this.previewLoaded = false;
    this.fullLoaded = false;
    this.touchStartY = null;
    this.scrollbarComp = 0;

    this.init();
  }

  /* ---------- is the widget actually visible on this device? ---------- */
  ZaymaFloatingVideo.prototype.visible = function () {
    return !!(this.root.offsetWidth || this.root.offsetHeight || this.root.getClientRects().length);
  };

  /* ---------- circular preview ---------- */
  ZaymaFloatingVideo.prototype.reveal = function () {
    this.root.classList.add('is-ready');
    if (this.autoplay && !prefersReducedMotion() && !constrainedConnection()) {
      this.startPreview();
    }
  };

  ZaymaFloatingVideo.prototype.startPreview = function () {
    var self = this;
    if (!this.preview || !this.previewSrc || this.previewLoaded) return;
    if (!this.visible()) return;

    idle(function () {
      if (self.previewLoaded || self.isOpen) return;
      self.previewLoaded = true;
      self.preview.setAttribute('src', self.previewSrc);

      // Cross-fade to the moving frame as soon as there are real pixels,
      // whichever signal arrives first.
      var show = function () {
        self.bubble.classList.add('is-playing');
      };
      self.preview.addEventListener('playing', show, { once: true });
      self.preview.addEventListener('loadeddata', function () {
        if (!self.preview.paused) show();
      });

      var attempt = self.preview.play();
      if (attempt && typeof attempt.then === 'function') {
        attempt.then(show).catch(function () {
          // Autoplay blocked — leave the poster frame and the play icon.
          self.bubble.classList.remove('is-playing');
        });
      }
    }, 3000);
  };

  ZaymaFloatingVideo.prototype.pausePreview = function () {
    if (this.preview && this.previewLoaded) {
      try {
        this.preview.pause();
      } catch (e) {}
    }
  };

  ZaymaFloatingVideo.prototype.resumePreview = function () {
    if (!this.preview || !this.previewLoaded) return;
    if (!this.autoplay || prefersReducedMotion()) return;
    var attempt = this.preview.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
  };

  /* ---------- scroll lock ----------
     Measure the scrollbar BEFORE hiding it and pad the document by the same
     amount, so the page behind the modal cannot reflow (which showed up as
     the product image jumping bigger). */
  ZaymaFloatingVideo.prototype.lockScroll = function () {
    var sw = window.innerWidth - document.documentElement.clientWidth;
    this.scrollbarComp = sw > 0 ? sw : 0;
    if (this.scrollbarComp) {
      this.prevPadRight = document.documentElement.style.paddingRight;
      document.documentElement.style.paddingRight = this.scrollbarComp + 'px';
    }
    document.documentElement.classList.add('zfv-locked');
    document.body.classList.add('zfv-locked');
  };

  ZaymaFloatingVideo.prototype.unlockScroll = function () {
    document.documentElement.classList.remove('zfv-locked');
    document.body.classList.remove('zfv-locked');
    if (this.scrollbarComp) {
      document.documentElement.style.paddingRight = this.prevPadRight || '';
      this.scrollbarComp = 0;
    }
  };

  /* ---------- sound ---------- */
  ZaymaFloatingVideo.prototype.unmute = function () {
    this.video.muted = false;
    try {
      this.video.volume = 1;
    } catch (e) {}
    if (this.hint) this.hint.hidden = true;
    clearTimeout(this.hintTimer);
    if (this.video.paused) {
      var p = this.video.play();
      if (p && typeof p.catch === 'function') p.catch(function () {});
    }
    this.syncButtons();
  };

  /* ---------- modal ---------- */
  ZaymaFloatingVideo.prototype.open = function () {
    if (this.isOpen) return;
    this.isOpen = true;
    this.lastFocus = document.activeElement;
    this.pausePreview();

    if (!this.fullLoaded) {
      this.fullLoaded = true;
      this.video.setAttribute('src', this.fullSrc);
    }

    this.modal.hidden = false;
    this.lockScroll();

    var self = this;
    window.requestAnimationFrame(function () {
      self.modal.classList.add('is-open');
    });

    this.video.muted = this.startMuted;
    if (!this.startMuted) {
      try {
        this.video.volume = 1;
      } catch (e) {}
    }
    try {
      this.video.currentTime = 0;
    } catch (e) {}
    this.syncButtons();

    var attempt = this.video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function () {
        // Playback with sound refused — fall back to muted playback.
        self.video.muted = true;
        self.syncButtons();
        self.showHint();
        var retry = self.video.play();
        if (retry && typeof retry.catch === 'function') retry.catch(function () {});
      });
    }

    this.showHint();

    var first = this.root.querySelector('.zfv__close');
    if (first) first.focus();
  };

  ZaymaFloatingVideo.prototype.showHint = function () {
    if (!this.hint) return;
    if (this.dialog.classList.contains('zfv--native')) return;
    if (!this.video.muted) {
      this.hint.hidden = true;
      return;
    }
    this.hint.hidden = false;
    var self = this;
    clearTimeout(this.hintTimer);
    this.hintTimer = setTimeout(function () {
      self.hint.hidden = true;
    }, HINT_MS);
  };

  ZaymaFloatingVideo.prototype.close = function () {
    if (!this.isOpen) return;
    this.isOpen = false;

    try {
      this.video.pause();
    } catch (e) {}

    this.modal.classList.remove('is-open');
    var self = this;
    var finish = function () {
      self.modal.hidden = true;
    };
    if (prefersReducedMotion()) {
      finish();
    } else {
      setTimeout(finish, 180);
    }

    this.unlockScroll();
    clearTimeout(this.hintTimer);
    if (this.hint) this.hint.hidden = true;

    this.resumePreview();
    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus();
    } else if (this.bubble) {
      this.bubble.focus();
    }
  };

  ZaymaFloatingVideo.prototype.syncButtons = function () {
    if (this.playPauseBtn) {
      var paused = this.video.paused;
      this.playPauseBtn.setAttribute('aria-label', paused ? this.labels.play : this.labels.pause);
      this.playPauseBtn.setAttribute('data-state', paused ? 'paused' : 'playing');
    }
    if (this.muteBtn) {
      var muted = this.video.muted;
      this.muteBtn.setAttribute('aria-label', muted ? this.labels.unmute : this.labels.mute);
      this.muteBtn.setAttribute('data-state', muted ? 'muted' : 'unmuted');
    }
  };

  /* ---------- focus trap ---------- */
  ZaymaFloatingVideo.prototype.trap = function (event) {
    var focusable = this.dialog.querySelectorAll('button:not([disabled]):not([hidden])');
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* ---------- wiring ---------- */
  ZaymaFloatingVideo.prototype.init = function () {
    var self = this;

    if (this.preview) {
      this.preview.muted = true;
      this.preview.loop = this.loop;
      this.preview.playsInline = true;
    }

    if (this.bubble) {
      this.bubble.addEventListener('click', function () {
        self.open();
      });
    }

    for (var i = 0; i < this.closeBtns.length; i++) {
      this.closeBtns[i].addEventListener('click', function () {
        self.close();
      });
    }

    if (this.playPauseBtn) {
      this.playPauseBtn.addEventListener('click', function () {
        if (self.video.paused) {
          var p = self.video.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        } else {
          self.video.pause();
        }
        self.syncButtons();
      });
    }

    if (this.muteBtn) {
      this.muteBtn.addEventListener('click', function () {
        if (self.video.muted) {
          self.unmute();
        } else {
          self.video.muted = true;
          self.syncButtons();
        }
      });
    }

    if (this.hint) {
      this.hint.addEventListener('click', function () {
        self.unmute();
      });
    }

    if (this.video) {
      this.video.addEventListener('play', function () {
        self.syncButtons();
      });
      this.video.addEventListener('pause', function () {
        self.syncButtons();
      });
      this.video.addEventListener('volumechange', function () {
        self.syncButtons();
      });
      // Reel behaviour: the first tap on the frame turns the sound ON.
      // After that, tapping toggles play/pause.
      this.video.addEventListener('click', function () {
        if (self.dialog.classList.contains('zfv--native')) return;
        if (self.video.muted) {
          self.unmute();
          return;
        }
        if (self.video.paused) {
          var p = self.video.play();
          if (p && typeof p.catch === 'function') p.catch(function () {});
        } else {
          self.video.pause();
        }
      });
    }

    document.addEventListener('keydown', function (e) {
      if (!self.isOpen) return;
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        self.close();
      } else if (e.key === 'Tab') {
        self.trap(e);
      }
    });

    // Swipe down on the reel to dismiss it.
    if (this.dialog) {
      this.dialog.addEventListener(
        'touchstart',
        function (e) {
          self.touchStartY = e.touches[0].clientY;
        },
        { passive: true }
      );
      this.dialog.addEventListener(
        'touchend',
        function (e) {
          if (self.touchStartY === null) return;
          var delta = e.changedTouches[0].clientY - self.touchStartY;
          self.touchStartY = null;
          if (delta > 90) self.close();
        },
        { passive: true }
      );
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        self.pausePreview();
        if (self.isOpen) {
          try {
            self.video.pause();
          } catch (e) {}
        }
      } else if (!self.isOpen) {
        self.resumePreview();
      }
    });

    if (this.delay > 0) {
      setTimeout(function () {
        self.reveal();
      }, this.delay);
    } else {
      this.reveal();
    }
  };

  function boot() {
    var root = document.getElementById(ROOT_ID);
    if (!root || root.getAttribute('data-zfv-init') === '1') return;
    if (!root.getAttribute('data-full-src')) return;
    root.setAttribute('data-zfv-init', '1');
    window.zaymaFloatingVideo = new ZaymaFloatingVideo(root);
  }

  ready(boot);

  // Theme editor: the section can be re-rendered without a page reload.
  document.addEventListener('shopify:section:load', function () {
    boot();
  });
})();
